import React from "react";
import {
  BookOpen,
  PenLine,
  Headphones,
  Mic,
  ClipboardList,
  Target,
  Compass,
  GraduationCap,
} from "lucide-react";

/* ======= Data ======= */
const EXAMS = [
  {
    code: "A2",
    level: "A2 · Basic User",
    name: "A2 Key",
    acronym: "KET",
    headline: "Construye una base sólida en inglés.",
    desc:
      "Practica el inglés de situaciones cotidianas, mejora tu comprensión y aprende a comunicarte con mayor seguridad en contextos sencillos.",
    image: "/images/cambridge-a2.jpg",
    alt: "Estudiante practicando vocabulario y ejercicios básicos de inglés",
  },
  {
    code: "B1",
    level: "B1 · Independent User",
    name: "B1 Preliminary",
    acronym: "PET",
    headline: "Comunícate con más confianza.",
    desc:
      "Desarrolla tus habilidades para comprender textos, expresar ideas y desenvolverte en situaciones habituales, mientras te familiarizas con el formato del examen.",
    image: "/images/cambridge-b1.jpg",
    alt: "Dos estudiantes practicando conversación en inglés",
  },
  {
    code: "B2",
    level: "B2 · Independent User",
    name: "B2 First",
    acronym: "FCE",
    headline: "Lleva tu inglés al siguiente nivel.",
    desc:
      "Practica las destrezas que necesitas para comunicarte con seguridad y trabaja con ejercicios orientados a las diferentes partes del examen.",
    image: "/images/cambridge-b2.jpg",
    alt: "Estudiante preparando ejercicios de comprensión y escritura en inglés",
  },
  {
    code: "C1",
    level: "C1 · Proficient User",
    name: "C1 Advanced",
    acronym: "CAE",
    headline: "Perfecciona tu inglés avanzado.",
    desc:
      "Trabaja con textos más complejos, amplía tus recursos lingüísticos y practica la expresión oral y escrita para desenvolverte en contextos académicos y profesionales.",
    image: "/images/cambridge-c1.jpg",
    alt: "Estudiante avanzado trabajando con textos y redacción en inglés",
  },
];

const PREP_ITEMS = [
  {
    Icon: ClipboardList,
    title: "Conoce el formato",
    desc: "Familiarízate con las partes del examen y con los diferentes tipos de ejercicios.",
  },
  {
    Icon: Target,
    title: "Practica las destrezas",
    desc: "Trabaja la comprensión lectora, la expresión escrita, la comprensión auditiva y la expresión oral.",
  },
  {
    Icon: Compass,
    title: "Desarrolla estrategias",
    desc: "Practica la organización del tiempo, la comprensión de instrucciones y las técnicas necesarias para afrontar cada prueba.",
  },
];

const SKILLS = [
  {
    Icon: BookOpen,
    title: "Reading",
    desc: "Practica la comprensión de textos, la identificación de información relevante y la interpretación del significado.",
  },
  {
    Icon: PenLine,
    title: "Writing",
    desc: "Trabaja la organización de ideas, la gramática, el vocabulario y la redacción de textos adecuados a cada nivel.",
  },
  {
    Icon: Headphones,
    title: "Listening",
    desc: "Entrena la comprensión de conversaciones, mensajes y audios, prestando atención a la información clave.",
  },
  {
    Icon: Mic,
    title: "Speaking",
    desc: "Practica la fluidez, la pronunciación, la interacción y la expresión de opiniones en inglés.",
  },
];

/* ======= Component ======= */
const CambridgeExams = () => (
  <section
    id="cambridge"
    aria-labelledby="cambridge-heading"
    style={{
      background: "#F4F1EA",
      padding: "clamp(60px, 7vw, 100px) 24px",
      scrollMarginTop: 90,
    }}
  >
    <div style={{ maxWidth: 1240, margin: "0 auto" }}>
      {/* BLOCK A — Header */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.05fr 1fr",
          gap: 44,
          alignItems: "center",
          background:
            "linear-gradient(135deg, #1A1352 0%, #2A1E7A 50%, #5446EA 100%)",
          borderRadius: 28,
          padding: "clamp(32px, 4.5vw, 56px)",
          color: "#fff",
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 30px 70px -25px rgba(26,19,82,0.38)",
        }}
        className="cambridge-header-grid"
      >
        <div style={{ position: "relative", zIndex: 1 }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: "rgba(255,255,255,0.12)",
              color: "#FFC56B",
              padding: "6px 14px",
              borderRadius: 999,
              fontSize: 11.5,
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              marginBottom: 18,
            }}
          >
            <GraduationCap size={13} /> Cambridge Exam Preparation
          </span>
          <h2
            id="cambridge-heading"
            className="h-display"
            style={{
              fontSize: "clamp(30px, 4.5vw, 54px)",
              color: "#fff",
              margin: "0 0 14px",
              lineHeight: 1.05,
            }}
          >
            Your English. <span style={{ color: "#FFB184" }}>Your Goals.</span>
            <br />
            <span className="text-grad-purple-light">Your Future.</span>
          </h2>
          <p
            style={{
              color: "rgba(255,255,255,0.9)",
              fontSize: 17,
              fontWeight: 500,
              margin: "0 0 16px",
            }}
          >
            Prepárate para tu próximo reto con Cambridge.
          </p>
          <p
            style={{
              color: "rgba(255,255,255,0.78)",
              fontSize: 15,
              lineHeight: 1.6,
              margin: "0 0 22px",
              maxWidth: 560,
            }}
          >
            En Active English puedes complementar tu aprendizaje del inglés con
            una preparación específica para los exámenes Cambridge. Familiarízate
            con el formato de las pruebas, practica las destrezas evaluadas y
            desarrolla estrategias para afrontar el examen con mayor confianza.
          </p>
          <div
            style={{
              display: "inline-flex",
              flexWrap: "wrap",
              gap: 10,
              color: "rgba(255,255,255,0.85)",
              fontSize: 13.5,
              fontWeight: 600,
              letterSpacing: "0.02em",
            }}
            aria-hidden="true"
          >
            <span>A2 Key</span>
            <span style={{ color: "#FF4F1A" }}>·</span>
            <span>B1 Preliminary</span>
            <span style={{ color: "#FF4F1A" }}>·</span>
            <span>B2 First</span>
            <span style={{ color: "#FF4F1A" }}>·</span>
            <span>C1 Advanced</span>
          </div>
        </div>

        <div style={{ position: "relative", zIndex: 1 }}>
          <div
            style={{
              borderRadius: 20,
              overflow: "hidden",
              aspectRatio: "4/3",
              boxShadow: "0 20px 50px -15px rgba(0,0,0,0.4)",
              background: "#0F0A33",
            }}
          >
            <img
              src="/images/cambridge-header.jpg"
              alt="Estudiantes adultos preparando un examen de inglés con ordenador y materiales de estudio"
              loading="lazy"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </div>
        </div>

        {/* subtle decorative accent */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: -70,
            right: -70,
            width: 220,
            height: 220,
            background:
              "radial-gradient(circle, rgba(255,79,26,0.28), rgba(255,79,26,0) 70%)",
            filter: "blur(6px)",
            pointerEvents: "none",
          }}
        />
      </div>

      {/* BLOCK B — Preparation info */}
      <div style={{ marginTop: 72, maxWidth: 820 }}>
        <h3
          className="h-display"
          style={{
            fontSize: "clamp(24px, 2.6vw, 34px)",
            color: "#1A1352",
            margin: "0 0 14px",
            lineHeight: 1.2,
          }}
        >
          Mucho más que aprender inglés:{" "}
          <span className="text-grad-purple">prepárate para tu objetivo</span>
        </h3>
        <p style={{ color: "#5F5A72", fontSize: 15.5, lineHeight: 1.65, margin: 0 }}>
          Aprender inglés y preparar un examen oficial son objetivos complementarios.
          Mientras las clases de inglés general te ayudan a desarrollar tus
          conocimientos y comunicarte con mayor seguridad, la preparación Cambridge
          te permite familiarizarte con las tareas, los tipos de ejercicios y las
          exigencias específicas de cada examen.
        </p>
      </div>

      <div
        style={{
          marginTop: 32,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 18,
        }}
      >
        {PREP_ITEMS.map(({ Icon, title, desc }) => (
          <div
            key={title}
            style={{
              background: "#fff",
              border: "1px solid rgba(26,19,82,0.07)",
              borderRadius: 18,
              padding: "22px 22px 24px",
              display: "flex",
              gap: 14,
              alignItems: "flex-start",
            }}
          >
            <span
              style={{
                width: 40,
                height: 40,
                flexShrink: 0,
                borderRadius: 12,
                background: "rgba(84,70,234,0.1)",
                color: "#5446EA",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              aria-hidden="true"
            >
              <Icon size={20} strokeWidth={1.8} />
            </span>
            <div>
              <h4
                style={{
                  margin: "2px 0 6px",
                  color: "#1A1352",
                  fontSize: 15.5,
                  fontWeight: 700,
                }}
              >
                {title}
              </h4>
              <p style={{ margin: 0, color: "#5F5A72", fontSize: 13.5, lineHeight: 1.55 }}>
                {desc}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* BLOCK C — Four exam cards */}
      <div
        style={{
          marginTop: 56,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 22,
        }}
        role="list"
        aria-label="Exámenes Cambridge disponibles para preparación"
      >
        {EXAMS.map((e) => (
          <article
            key={e.code}
            role="listitem"
            className="cambridge-card"
            style={{
              background: "#fff",
              borderRadius: 22,
              overflow: "hidden",
              border: "1px solid rgba(26,19,82,0.07)",
              boxShadow: "0 14px 36px -22px rgba(26,19,82,0.22)",
              display: "flex",
              flexDirection: "column",
              transition: "transform 220ms ease, box-shadow 220ms ease",
            }}
          >
            <div style={{ position: "relative", aspectRatio: "4/3", overflow: "hidden" }}>
              <img
                src={e.image}
                alt={e.alt}
                loading="lazy"
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
              <span
                style={{
                  position: "absolute",
                  top: 12,
                  left: 12,
                  background: "#1A1352",
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: 11.5,
                  letterSpacing: "0.08em",
                  padding: "5px 11px",
                  borderRadius: 999,
                }}
                aria-hidden="true"
              >
                {e.code}
              </span>
            </div>
            <div
              style={{
                padding: "20px 22px 22px",
                display: "flex",
                flexDirection: "column",
                gap: 10,
                flex: 1,
              }}
            >
              <h4
                className="h-display"
                style={{ margin: 0, fontSize: 20, color: "#1A1352", lineHeight: 1.15 }}
              >
                {e.name}{" "}
                <span style={{ color: "#5F5A72", fontWeight: 600, fontSize: 14 }}>
                  ({e.acronym})
                </span>
              </h4>
              <div style={{ color: "#5446EA", fontSize: 11.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                {e.level}
              </div>
              <p style={{ margin: "4px 0 0", color: "#1A1352", fontWeight: 600, fontSize: 14.5, lineHeight: 1.35 }}>
                {e.headline}
              </p>
              <p style={{ margin: 0, color: "#5F5A72", fontSize: 13.5, lineHeight: 1.55 }}>
                {e.desc}
              </p>
              <ul
                style={{
                  margin: "6px 0 0",
                  padding: 0,
                  listStyle: "none",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 6,
                }}
                aria-label={`Áreas de preparación de ${e.name}`}
              >
                {["Reading", "Writing", "Listening", "Speaking"].map((tag) => (
                  <li
                    key={tag}
                    style={{
                      background: "rgba(84,70,234,0.08)",
                      color: "#5446EA",
                      padding: "4px 10px",
                      borderRadius: 999,
                      fontSize: 11.5,
                      fontWeight: 600,
                    }}
                  >
                    {tag}
                  </li>
                ))}
              </ul>
            </div>
          </article>
        ))}
      </div>

      {/* BLOCK D — Four skills */}
      <div style={{ marginTop: 72 }}>
        <h3
          className="h-display"
          style={{ fontSize: "clamp(24px, 2.6vw, 34px)", color: "#1A1352", margin: "0 0 10px" }}
        >
          Train every skill. <span className="text-grad-purple">Build your confidence.</span>
        </h3>
        <p style={{ color: "#5F5A72", fontSize: 15, lineHeight: 1.6, margin: "0 0 26px", maxWidth: 720 }}>
          Una preparación equilibrada requiere trabajar las diferentes competencias
          lingüísticas que intervienen en los exámenes Cambridge.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 16,
          }}
        >
          {SKILLS.map(({ Icon, title, desc }) => (
            <div
              key={title}
              style={{
                background: "#F3F6FA",
                border: "1px solid rgba(26,19,82,0.06)",
                borderRadius: 18,
                padding: "22px 22px 24px",
              }}
            >
              <span
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 12,
                  background: "#fff",
                  color: "#FF4F1A",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 14,
                  boxShadow: "0 6px 14px -8px rgba(255,79,26,0.4)",
                }}
                aria-hidden="true"
              >
                <Icon size={20} strokeWidth={1.8} />
              </span>
              <h4
                style={{
                  margin: "0 0 6px",
                  color: "#1A1352",
                  fontSize: 15,
                  fontWeight: 800,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                }}
              >
                {title}
              </h4>
              <p style={{ margin: 0, color: "#5F5A72", fontSize: 13.5, lineHeight: 1.55 }}>
                {desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* BLOCK E — Connection with general English */}
      <div
        style={{
          marginTop: 56,
          background: "#fff",
          borderRadius: 24,
          padding: "clamp(28px, 3.5vw, 44px)",
          border: "1px solid rgba(26,19,82,0.08)",
          borderLeft: "6px solid #FF4F1A",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <h3
          className="h-display"
          style={{ margin: 0, fontSize: "clamp(22px, 2.4vw, 30px)", color: "#1A1352", lineHeight: 1.2 }}
        >
          Build your English. <span className="text-grad-purple">Prepare for your future.</span>
        </h3>
        <p style={{ margin: "6px 0 0", color: "#5F5A72", fontSize: 15, lineHeight: 1.6, maxWidth: 820 }}>
          En Active English puedes continuar desarrollando tu nivel de inglés general
          desde A1 hasta C1. Si tu objetivo es preparar un examen Cambridge, podrás
          orientar tu aprendizaje hacia las destrezas y el formato específicos de la
          certificación que te interese.
        </p>
        <p style={{ margin: "8px 0 0", color: "#5446EA", fontSize: 15, fontWeight: 700, letterSpacing: "0.01em" }}>
          English for life. English for your goals.
        </p>
      </div>
    </div>

    <style>{`
      .cambridge-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 26px 54px -22px rgba(26,19,82,0.3);
      }
      @media (prefers-reduced-motion: reduce) {
        .cambridge-card { transition: none !important; }
        .cambridge-card:hover { transform: none !important; }
      }
      @media (max-width: 900px) {
        .cambridge-header-grid {
          grid-template-columns: 1fr !important;
          gap: 28px !important;
        }
      }
    `}</style>
  </section>
);

export default CambridgeExams;
